<template>
  <div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div v-else class="container">
      <div class="header">
        <div class="d-flex align-center">
          <v-btn flat icon class="back-button" @click="goBack">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <h1 class="screen-title">{{ $adminT('events.heading', 'Events') }}</h1>
        </div>
      </div>
      <div>
        <v-tabs v-model="tab" slider-color="transparent" height="36" class="tabs">
          <v-tab class="tab">{{ $adminT('events.live', 'Live') }}</v-tab>
          <v-tab class="tab">{{ $adminT('events.past', 'Past') }}</v-tab>
        </v-tabs>
        <div v-if="tab === 0" class="event-container">
          <event-card
            v-for="event in liveEvents"
            :key="event.id"
            :event="event"
            :organization-slug="organizationSlug"
            :organization-type="organizationType"
          />
        </div>
        <div v-else class="event-container">
          <event-card
            v-for="event in pastEvents"
            :key="event.id"
            :event="event"
            :organization-slug="organizationSlug"
            :organization-type="organizationType"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import moment from 'moment'
import { min, max, round, sumBy } from 'lodash'

import { GET_ORGANIZATION_WORKING_EVENTS } from '../../graphql/event.graphql'
import { GET_THEATER_WORKING_EVENT_TEMPLATES } from '../../graphql/eventTemplate.graphql'
import { OrganizationType } from '@/types'

import eventCard from './event-card.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'organization-events-list',
  components: { eventCard },
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      events: [],
      tab: null,
      organizationType: null,
    }
  },
  computed: {
    isTheater() {
      return this.$route.path.includes('theater-organizations')
    },
    pastEvents() {
      return this.events
        .filter(({ to }) => moment(to).isBefore(moment()))
        .sort((event1, event2) => new Date(event2.to) - new Date(event1.to))
    },
    liveEvents() {
      return this.events
        .filter(({ to }) => moment(to).isAfter(moment()))
        .sort((event1, event2) => new Date(event1.from) - new Date(event2.from))
    },
  },
  created() {
    if (this.isTheater) {
      this.organizationType = OrganizationType.THEATER
      this.fetchTheaterTemplates()
    }
  },
  apollo: {
    getOrganizationWorkingEvents: {
      query: GET_ORGANIZATION_WORKING_EVENTS,
      skip() {
        return this.isTheater
      },
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      result({ data: { getOrganizationWorkingEvents }, loading }) {
        if (!loading) {
          const events = getOrganizationWorkingEvents || []
          this.organizationType = (events[0] && events[0].organization && events[0].organization.type) || null
          this.events = events
        }
      },
    },
  },
  methods: {
    goBack() {
      this.$router.push('/')
    },
    async fetchTheaterTemplates() {
      const { data } = await this.$apollo.query({
        query: GET_THEATER_WORKING_EVENT_TEMPLATES,
        variables: { organizationSlug: this.organizationSlug },
      })
      const templates = (data && data.getTheaterWorkingEventTemplates) || []
      this.events = this.mapTemplatesToEventCards(templates)
    },
    mapTemplatesToEventCards(templates) {
      return templates.map(template => {
        const performances = template.events || []
        const dates = performances.reduce(
          (acc, p) => ({
            from: acc.from ? min([acc.from, p.from]) : p.from,
            to: acc.to ? max([acc.to, p.to]) : p.to,
          }),
          { from: null, to: null },
        )
        const firstPerformance = performances[0]

        return {
          id: template.id,
          title: template.title,
          cover: template.cover || (firstPerformance && firstPerformance.cover),
          from: dates.from,
          to: dates.to,
          extraModules: (firstPerformance && firstPerformance.extraModules) || {},
          location: (firstPerformance && firstPerformance.location) || {},
          totalTicketsSold: sumBy(performances, 'totalTicketsSold'),
          totalNumberOfTickets: sumBy(performances, 'totalNumberOfTickets'),
          totalEarnings: round(sumBy(performances, 'totalEarnings'), 2),
        }
      })
    },
  },
}
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  align-self: flex-start;
}

.screen-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 800;
  flex-basis: 15%;
  font-size: 3rem;
  align-self: flex-start;
  width: 13.25rem;
  color: #ff4c00;
  margin-right: 3rem;
}
p {
  display: inline;
}
.tab {
  font-family: 'Raleway', sans-serif;
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.5;
}

.tab >>> .v-tabs__item {
  padding: 0.375rem 0.75rem;
  color: #0277bd;
}

.tab >>> .v-tabs__item--active {
  background: #0277bd;
  border-radius: 0.5rem;
  color: white;
}

.tabs >>> .v-tabs__container {
  flex: 0 1 auto;
  background: #f0f6fa;
  border-radius: 0.5rem;
}
.event-container {
  display: flex;
  flex-direction: column;
  max-width: 42.75rem;
  gap: 1rem;
}
@media screen and (max-width: 500px) {
  .event-container {
    gap: 0;
  }
}
.container {
  display: flex;
  justify-content: center;
}
.tabs {
  margin-bottom: 1.5rem;
  padding-top: 1.25rem;
}
.back-button {
  margin-bottom: 1.5rem;
}
@media screen and (max-width: 801px) {
  .container {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }
  .header {
    margin-bottom: 1.5rem;
  }
  .screen-title {
    margin-bottom: 0;
    padding: 0 !important;
  }
  .tabs {
    padding-top: 0 !important;
  }
  .back-button {
    margin-bottom: 0;
    margin-top: 16px;
  }
}
</style>
